import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, batch, useSelector } from 'react-redux';

import { axiosAPI } from '../../axiosAPI';

import ContentOptimizerService from '../../../services/contentOptimizer.service';

import { useOrganizationSlug } from '../useOrganizationSlug';
import { useSwitchOptimizerTabs } from './useChangeOptimizerTabs';

import { setRankabilityLoading } from '../../../userBrowserSettings/store/browserSettings.actions';

import { contentOptimizerPathes } from '../../../constants/queryPathes';
import { aiInstanceTypes, aiStatus } from '../../../constants/aiOptimizerConstants';
import { statusCodes } from '../../../constants/statusCodes';

import { getAxiosHeaders } from '../../helpers/getAxiosHeaders';
import { openBasicErrorNotification } from '../../helpers/openBasicErrorNotification';
import { showAiResultMessage } from '../../helpers/aiOptimizerHelpers';
import { transformContentData } from '../../helpers/editor/slateToHtml';

import {
    setAIContentLoadingField,
    setAIContentLoadingProgress,
} from '../../../components/pages/contentOptimizer/contentPage/content/briefTab/content/tabsContent/store/contentBriefsOutline.actions';
import { updateOpenAiBriefsUsed } from '../../../components/pages/account/billing/store/billingSettings.actions';
import { aiContentFields } from '../../../components/pages/contentOptimizer/contentPage/content/briefTab/contentBriefsConstants';
import {
    selectAIContentLoadingField,
    selectAIContentLoadingProgress,
} from '../../../components/pages/contentOptimizer/contentPage/content/briefTab/content/tabsContent/store/contentBriefsOutline.selectors';
import {
    updateCurrentEditorContent,
    updateEditorBasicData,
} from '../../../components/common/tiptapEditor/store/tiptapEditor.actions';
import { editorTypes } from '../../../components/common/tiptapEditor/constants/tiptapEditorConstants';
import {
    getVersionBasicData,
    parseEditorContent,
} from '../../../components/common/tiptapEditor/utils/versionHelpers';

export const useTrackProgressAIContent = (contentOptimizerId) => {
    const dispatch = useDispatch();
    const aiContentLoadingField = useSelector(selectAIContentLoadingField);
    const isAIContentGeneration = aiContentLoadingField === aiContentFields.OPTIMIZER;
    const aiContentLoadingProgress = useSelector(selectAIContentLoadingProgress);

    const organizationSlug = useOrganizationSlug();
    const { switchToOptimizerContentTab } = useSwitchOptimizerTabs();

    const getAndUpdateBasicData = () => {
        if (!contentOptimizerId || !organizationSlug) return;

        ContentOptimizerService.getContentOptimizerVersions(contentOptimizerId, {
            slug: organizationSlug,
            active: true,
        })
            .then((result) => {
                if (result?.status === statusCodes.success && result.data?.length > 0) {
                    const currentVersion = result.data[0];

                    batch(() => {
                        // Updating current editor content
                        dispatch(
                            updateCurrentEditorContent({
                                editorType: editorTypes.OPTIMIZER,
                                content: parseEditorContent(
                                    transformContentData(
                                        currentVersion?.content,
                                        currentVersion?.active
                                    ),
                                    editorTypes.OPTIMIZER
                                ),
                            })
                        );
                        dispatch(updateEditorBasicData(getVersionBasicData(currentVersion)));
                    });
                }
            })
            .catch(() => {
                openBasicErrorNotification();
            });
    };

    const getGenerationStatus = useCallback(
        (initialCheck = false) => {
            if (!contentOptimizerId) return;

            const optimizerIds = [contentOptimizerId];

            axiosAPI
                .get(contentOptimizerPathes.getAiContentStatuses, {
                    ...getAxiosHeaders(),
                    params: { optimizer_ids: optimizerIds.join() || '' },
                })
                .then((result) => {
                    const { generation_statuses, quota } = result?.data || {};
                    const currentAiContentObject = (generation_statuses || []).find(
                        (item) => item.content_optimizer_id === contentOptimizerId
                    );

                    if (currentAiContentObject) {
                        const generationStatus = currentAiContentObject['generation_status'];
                        const generationProgress = currentAiContentObject['generation_progress'];

                        if (!initialCheck) {
                            if (
                                generationProgress &&
                                generationProgress !== aiContentLoadingProgress
                            ) {
                                dispatch(setAIContentLoadingProgress(generationProgress));
                            }

                            if (generationStatus?.length > 0) {
                                showAiResultMessage(generationStatus, aiInstanceTypes.CONTENT);

                                if (generationStatus === aiStatus.COMPLETED) {
                                    switchToOptimizerContentTab();
                                    getAndUpdateBasicData();
                                }

                                if (aiStatus.FINAL_STATUSES.includes(generationStatus)) {
                                    clearInterval(intervalID.current);
                                    intervalID.current = null;
                                    batch(() => {
                                        dispatch(setRankabilityLoading(0));
                                        dispatch(setAIContentLoadingField(null));
                                        dispatch(setAIContentLoadingProgress(0));
                                    });
                                }
                            }

                            if (!!quota) {
                                dispatch(updateOpenAiBriefsUsed(quota.openai_briefs_used));
                            }
                        } else {
                            if (
                                [aiStatus.QUEUED, aiStatus.IN_PROGRESS].includes(generationStatus)
                            ) {
                                batch(() => {
                                    dispatch(setAIContentLoadingField(aiContentFields.OPTIMIZER));
                                    dispatch(setAIContentLoadingProgress(generationProgress));
                                });
                            }
                        }
                    }
                })
                .catch((_err) => {
                    openBasicErrorNotification();
                    clearInterval(intervalID.current);
                    intervalID.current = null;

                    batch(() => {
                        dispatch(setRankabilityLoading(0));
                        dispatch(setAIContentLoadingField(null));
                        dispatch(setAIContentLoadingProgress(0));
                    });
                });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [contentOptimizerId, dispatch]
    );

    const intervalID = useRef(null);

    /**
     * Runs only once in order to check if there is AI Content generation in progress.
     * If so, AI content field (aiContentFields.OPTIMIZER) and generation progress are set to the
     * redux store, which invokes the next useEffect. It means, that the progress bar is visible.
     */
    useEffect(() => {
        getGenerationStatus(true);
    }, []);

    useEffect(() => {
        if (isAIContentGeneration && !intervalID.current) {
            intervalID.current = setInterval(() => getGenerationStatus(), 5 * 1000);
        } else if (!isAIContentGeneration && intervalID.current) {
            clearInterval(intervalID.current);
        }

        return () => {
            if (intervalID.current) {
                clearInterval(intervalID.current);
                intervalID.current = null;
            }
        };
    }, [isAIContentGeneration, getGenerationStatus]);
};
